import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { api } from '../services/api';
import { audioEngine } from '../services/audioEngine';
import { useWorldStore } from '../state/worldStore';
import { API_BASE, VIDEO_OFF } from '../config';
import { CITY_BILLBOARDS } from '../cityBillboards';
import { sky } from '../render/skyState';

// A SCREEN IS NOT EXPOSED LIKE THE STREET (Khari, 2026-10-23: "the track images
// on the billboards are burnt looking. It may just be the night shading").
// toneMapped={false} does not keep a picture out of the grade: the post pass
// (render/effects GradeEffect) runs the whole frame through the hour's exposure
// and a filmic curve, and at night that exposure climbs to ~1.12 (nearly 2x into
// the curve) with a warm push on highlights. Most artwork survives it; a dark
// cover with bright gold in it blows out and goes orange. So once the night's
// exposure rises above the day's, a board's picture is scaled back to its
// daytime brightness: by day nothing changes. ?boardgain=0 turns this off (to
// compare).
const DAY_EXPOSURE = 0.95;
const BOARD_GAIN_ON = typeof window === 'undefined' || new URLSearchParams(window.location.search).get('boardgain') !== '0';

// Route GCS assets (image or video) through the API proxy so WebGL can sample
// them (textures/VideoTextures are CORS-gated). Handles gs:// and https GCS.
function proxied(url) {
  try {
    if (url.startsWith('gs://')) {
      const rest = url.slice(5);
      const i = rest.indexOf('/');
      return `${API_BASE}/api/assets/file?bucket=${encodeURIComponent(rest.slice(0, i))}&name=${encodeURIComponent(rest.slice(i + 1))}`;
    }
    const u = new URL(url);
    if (u.hostname === 'storage.googleapis.com') {
      const parts = u.pathname.replace(/^\//, '').split('/');
      const bucket = parts.shift();
      const name = parts.map(decodeURIComponent).join('/');
      return `${API_BASE}/api/assets/file?bucket=${encodeURIComponent(bucket)}&name=${encodeURIComponent(name)}`;
    }
  } catch { /* passthrough */ }
  return url;
}

function parseAspect(a) {
  if (typeof a === 'string' && a.includes(':')) { const [w, h] = a.split(':').map(Number); if (w && h) return w / h; }
  return 16 / 9;
}

// How far apart the boards are allowed to start their videos once the camera
// settles. See the note on `videoOk` below.
const VIDEO_STAGGER_MS = 900;

function Billboard({ cfg, content, order = 0, selected, djTex = null, djAspect = 16 / 9 }) {
  const EDIT = useWorldStore((s) => s.adminTool) === 'bb'; // billboard placement (AdminPanel)
  // While the radio DJ is live on this board, show the Anam stream in place of
  // the track art (the content texture stays loaded underneath for instant revert).
  const djOn = !!djTex && cfg.dj;
  const load = () => {
    try { const s = JSON.parse(localStorage.getItem(`bb:${cfg.id}`) || 'null'); if (s) return s; } catch { /* noop */ }
    return { pos: cfg.pos, rotY: cfg.rotY, height: cfg.height };
  };
  const [xf, setXf] = useState(load);
  const [tex, setTex] = useState(null);
  const [aspect, setAspect] = useState(content?.aspect || (content?.type === 'video' ? 16 / 9 : 0.66));
  const videoRef = useRef(null);
  const camRef = useRef(null);

  // Build the texture — a still image (featured track) or a looping muted
  // video (music-video clip), both proxied for CORS.
  //
  // VIDEO WAITS FOR THE CAMERA. Three or four boards each fetching an mp4,
  // starting a decoder and then uploading a fresh frame EVERY frame is a heavy
  // thing to switch on during a fly-in, which is already the busiest moment in
  // the world. Until the camera settles the board shows the track's still — one
  // texture, uploaded once — and swaps to the animation after. The boards are
  // distant and small during the fly-in, so the still costs nothing visually,
  // and this keeps the rule that animated artwork wins wherever it exists.
  // SETTLING IS A MOMENT; STARTING EVERYTHING AT IT IS A STALL.
  //
  // Waiting for camSettled correctly kept this work out of the fly-in, but every
  // board then began in the SAME frame — several video fetches and decoders at
  // once, alongside the car fleet doing the same. On a warm cache that is
  // invisible; on a first visit it froze the moment the camera landed, which is
  // the worst possible moment for it. Refreshing "fixed" it only because
  // everything was then cached.
  //
  // So the boards queue up behind each other instead of racing. Each waits its
  // turn, and the still stays on screen until its own video is ready — nothing
  // is blank, it just fills in over a couple of seconds.
  const [videoOk, setVideoOk] = useState(false);
  useEffect(() => {
    // Staggered from MOUNT, not from camera-settle (2026-08-02): decoders now
    // spin up behind the loading overlay, so the boards are already playing
    // when the fly-in reveals them instead of popping alive afterwards. The
    // stagger itself stays — starting every decoder in one frame is the stall
    // it always was, wherever it happens.
    const t = setTimeout(() => setVideoOk(true), VIDEO_STAGGER_MS * order);
    return () => clearTimeout(t);
  }, [order]);

  useEffect(() => {
    if (!content) return undefined;
    if (content.type === 'video' && videoOk) {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.src = proxied(content.videoUrl);
      video.loop = true; video.muted = true; video.playsInline = true; video.preload = 'auto';
      video.play().catch(() => {});
      video.addEventListener('loadedmetadata', () => { if (video.videoWidth) setAspect(video.videoWidth / video.videoHeight); });
      const t = new THREE.VideoTexture(video);
      t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 16;
      videoRef.current = video;
      setTex(t);
      return () => { video.pause(); video.removeAttribute('src'); video.load(); t.dispose(); };
    }
    // No still to fall back on (a HypeClip carries no poster) — leave the board
    // bare for the moment rather than start its decoder early.
    if (!content.imageUrl) return undefined;
    const t = new THREE.TextureLoader().load(
      proxied(content.imageUrl),
      (img) => { img.colorSpace = THREE.SRGBColorSpace; img.anisotropy = 16; img.needsUpdate = true; if (img.image?.width) setAspect(img.image.width / img.image.height); },
      undefined,
      () => console.warn('[billboard] art failed:', content.imageUrl),
    );
    t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 16;
    setTex(t);
    return () => t.dispose();
  }, [content?.type, content?.imageUrl, content?.videoUrl, videoOk]);

  // Placement editor (only the selected panel listens): camera-relative arrows,
  // u/j height, [ ] rotate, -/= size, r reset. Auto-saves + logs paste-ready.
  useEffect(() => {
    if (!EDIT || !selected) return undefined;
    const onKey = (e) => {
      setXf((s) => {
        const [x, y, z] = s.pos;
        let fx = 0, fz = -1, rx = 1, rz = 0;
        const cam = camRef.current;
        if (cam) { const d = new THREE.Vector3(); cam.getWorldDirection(d); d.y = 0; if (d.lengthSq() > 1e-6) { d.normalize(); fx = d.x; fz = d.z; rx = -d.z; rz = d.x; } }
        const dist = cam ? Math.max(2, cam.position.distanceTo(new THREE.Vector3(x, y, z))) : 8;
        const step = (e.shiftKey ? 0.01 : 0.05) * dist;
        let next = s;
        if (e.key === 'ArrowLeft') next = { ...s, pos: [x - rx * step, y, z - rz * step] };
        else if (e.key === 'ArrowRight') next = { ...s, pos: [x + rx * step, y, z + rz * step] };
        else if (e.key === 'ArrowUp') next = { ...s, pos: [x + fx * step, y, z + fz * step] };
        else if (e.key === 'ArrowDown') next = { ...s, pos: [x - fx * step, y, z - fz * step] };
        else if (e.key === 'u') next = { ...s, pos: [x, y + step * 0.6, z] };
        else if (e.key === 'j') next = { ...s, pos: [x, y - step * 0.6, z] };
        else if (e.key === '[') next = { ...s, rotY: s.rotY + 0.06 };
        else if (e.key === ']') next = { ...s, rotY: s.rotY - 0.06 };
        else if (e.key === '-') next = { ...s, height: Math.max(2, s.height - 0.5) };
        else if (e.key === '=') next = { ...s, height: s.height + 0.5 };
        else if (e.key === 'r') { localStorage.removeItem(`bb:${cfg.id}`); return { pos: cfg.pos, rotY: cfg.rotY, height: cfg.height }; }
        else return s;
        e.preventDefault();
        const r = (v) => Math.round(v * 100) / 100;
        localStorage.setItem(`bb:${cfg.id}`, JSON.stringify(next));
        console.log(`{ id: '${cfg.id}', pos: [${next.pos.map(r).join(', ')}], rotY: ${r(next.rotY)}, height: ${r(next.height)}, type: '${cfg.type || 'image'}' },`);
        return next;
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [EDIT, selected, cfg]);

  const picMat = useRef();
  useFrame(({ camera }) => {
    camRef.current = camera;
    if (picMat.current) picMat.current.color.setScalar(BOARD_GAIN_ON ? Math.min(1, DAY_EXPOSURE / Math.max(0.01, sky.exposure || 1)) : 1);
  });

  // Render if we have real content, or if the DJ is taking this board over.
  if ((!tex || !content) && !djOn) return null;
  const map = djOn ? djTex : tex;
  const width = xf.height * (djOn ? djAspect : aspect);

  // Click: a track plays in the city player; a video unmutes and plays with
  // sound (ambient ducks). Both are user gestures, so audio is allowed.
  const onClick = (e) => {
    e.stopPropagation();
    if (!content) return; // DJ-only takeover with no underlying track — nothing to play
    if (content.live) return; // the now-playing board IS the station — don't double it
    // A video that carries a track (animated artwork) plays the track's audio;
    // a bare music-video clip plays its own sound. Both route through the
    // audio engine so only one source ever sounds.
    if (content.type === 'video' && content.track?.audio_url) { audioEngine.playTrack(content.track); return; }
    if (content.type === 'video') {
      if (videoRef.current) audioEngine.playVideo(videoRef.current);
    } else if (content.track?.audio_url) {
      audioEngine.playTrack(content.track);
    }
  };

  return (
    <group position={xf.pos} rotation={[0, xf.rotY, 0]}>
      <mesh position={[0, 0, -0.05]}>
        <planeGeometry args={[width + 0.4, xf.height + 0.4]} />
        <meshBasicMaterial color={selected ? '#4fd1ff' : '#0c0c10'} toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
      <mesh
        onClick={onClick}
        onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { document.body.style.cursor = 'auto'; }}
      >
        <planeGeometry args={[width, xf.height]} />
        <meshBasicMaterial ref={picMat} map={map} toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

export default function CityBillboards() {
  const [tracks, setTracks] = useState([]);
  const [videos, setVideos] = useState([]);
  const [sel, setSel] = useState(0);
  const EDIT = useWorldStore((s) => s.adminTool) === 'bb'; // billboard placement (AdminPanel)

  // The station's current song → the center (now-playing) billboard.
  const nowPlaying = useWorldStore((s) => s.nowPlaying);

  // Radio DJ cameo: while the host is live, its hidden <video> (DjBillboardHost)
  // is sampled into one shared VideoTexture reused across the DJ boards.
  const djPhase = useWorldStore((s) => s.djCameo.phase);
  const djVideoEl = useWorldStore((s) => s.djCameo.videoEl);
  const djLive = djPhase === 'live' && !!djVideoEl;
  const [djAspect, setDjAspect] = useState(16 / 9);
  const djTex = useMemo(() => {
    if (!djLive || !djVideoEl) return null;
    const t = new THREE.VideoTexture(djVideoEl);
    t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 16;
    return t;
  }, [djLive, djVideoEl]);
  useEffect(() => () => { djTex?.dispose(); }, [djTex]);
  // Track the stream's real framing so the board isn't stretched.
  useEffect(() => {
    if (!djLive || !djVideoEl) return undefined;
    const read = () => { if (djVideoEl.videoWidth) setDjAspect(djVideoEl.videoWidth / djVideoEl.videoHeight); };
    read();
    djVideoEl.addEventListener('loadedmetadata', read);
    djVideoEl.addEventListener('resize', read);
    return () => { djVideoEl.removeEventListener('loadedmetadata', read); djVideoEl.removeEventListener('resize', read); };
  }, [djLive, djVideoEl]);

  useEffect(() => {
    api.featuredTracks().then((d) => setTracks(d?.tracks || [])).catch(() => setTracks([]));
    api.musicVideos().then((d) => setVideos(d?.videos || d?.music_videos || (Array.isArray(d) ? d : []))).catch(() => setVideos([]));
  }, []);

  useEffect(() => {
    if (!EDIT) return undefined;
    const onKey = (e) => { const n = parseInt(e.key, 10); if (!isNaN(n) && n >= 1 && n <= CITY_BILLBOARDS.length) setSel(n - 1); };
    window.addEventListener('keydown', onKey);
    console.log('[bb] edit mode — keys 1..N select, arrows/u/j move, [ ] rotate, -/= size, r reset');
    return () => window.removeEventListener('keydown', onKey);
  }, [EDIT]);

  // The center screen mirrors the station's CURRENT song. Its cover art (square
  // for most songs), and — if the track has an animated artwork video — that
  // video, muted and looping (the station carries the audio). We match the
  // now-playing track_id against the featured list to find its animation.
  const npCurrent = nowPlaying?.current;
  const nowPlayingContent = useMemo(() => {
    if (!npCurrent || npCurrent.item_type !== 'track' || !npCurrent.track_id) return null;
    const match = tracks.find((t) => String(t.track_id) === String(npCurrent.track_id));
    const anim = npCurrent.artwork_animation_url || match?.artwork_animation_url || match?.artwork_animated_url;
    const base = { title: npCurrent.title, artist: npCurrent.avatar_name, live: true };
    // live: true → this IS the station's current track; clicking must NOT start
    // a second copy (see onClick guard).
    const art = npCurrent.artwork_url || match?.artwork_url;
    // The still travels WITH the animation so the board has something cheap to
    // show during the fly-in — see the texture effect. Animated still wins once
    // the camera settles; this only decides what is on screen before that.
    if (anim && !VIDEO_OFF) return { ...base, type: 'video', videoUrl: anim, imageUrl: art, aspect: 1 };
    return art ? { ...base, type: 'image', imageUrl: art, aspect: 1 } : null;
  }, [npCurrent?.track_id, tracks]);

  // Resolve each billboard's content: the now-playing board mirrors the station;
  // a 'video' board cycles music-video clips; else a featured-track poster.
  const contents = useMemo(() => {
    // START SOMEWHERE RANDOM. These walked the pools from index 0, so the two
    // fixed centre boards always showed whichever two items the API happened to
    // return first — the same pictures every session, for as long as the
    // catalogue's order held. Offsetting the start rotates which of the pool
    // each board lands on without changing that they each get a different one.
    //
    // Per LOAD rather than on a timer, like the HypeClips and unlike house fill:
    // these boards carry playing video, and swapping sources under them mid-visit
    // stutters the most prominent screens in the city for no real gain.
    let vi = Math.floor(Math.random() * Math.max(1, videos.length));
    let ti = Math.floor(Math.random() * Math.max(1, tracks.length));
    return CITY_BILLBOARDS.map((cfg) => {
      if (cfg.nowPlaying) return nowPlayingContent;
      // Stills test: 'video' boards fall through to the track-poster pool —
      // a music-video clip has no still of its own, but a track always does.
      if (cfg.type === 'video' && !VIDEO_OFF) {
        const v = videos[vi++ % Math.max(1, videos.length)];
        return v && (v.gcs_url) ? { type: 'video', videoUrl: v.gcs_url, aspect: parseAspect(v.aspect_ratio), title: v.title, artist: v.avatar_name } : null;
      }
      const t = tracks[ti++ % Math.max(1, tracks.length)];
      if (!t) return null;
      // RULE: prefer the animated artwork (a looping mp4) when the track has
      // one; it plays silently and clicking still plays the track's audio.
      const anim = VIDEO_OFF ? null : (t.artwork_animation_url || t.artwork_animated_url);
      if (anim) return { type: 'video', videoUrl: anim, imageUrl: t.artwork_url, aspect: 1, title: t.title, track: t };
      return t.artwork_url ? { type: 'image', imageUrl: t.artwork_url, track: t } : null;
    });
  }, [tracks, videos, nowPlayingContent]);

  if (!tracks.length && !videos.length && !djLive && !nowPlayingContent) return null;
  return (
    <group>
      {CITY_BILLBOARDS.map((cfg, i) => {
        const dj = djLive && cfg.dj;
        // Skip only boards that have neither content nor a DJ takeover.
        if (!contents[i] && !dj) return null;
        return (
          <Billboard
            key={cfg.id}
            cfg={cfg}
            content={contents[i]}
            order={i}
            selected={EDIT && sel === i}
            djTex={dj ? djTex : null}
            djAspect={djAspect}
          />
        );
      })}
    </group>
  );
}
